import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InstantPick } from '../components/InstantPick'; // [FLUID-8 2026-10-12]
import { useSearchHighlight } from '../hooks/useSearchHighlight'; // [SEARCH-HIGHLIGHT 2026-09-29]
import { createPortal } from 'react-dom';
import styled from 'styled-components'; // [FLUID-3 2026-10-12] keyframes no longer used (the popIn went)
import { lgSurface, lgPress, lgButtonPrimary, lgButtonSecondary, lgSunken, lgTile, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { useAuth } from '../contexts/AuthContext';
import { SECTIONS, SECTION_GROUPS } from '../config/sections';
import { confirmDialog } from '../components/dialogService';
import { toastSuccess, toastError } from '../components/toastService';
import GlassSelect from '../components/GlassSelect';
import { TableSkeleton } from '../components/Skeleton';
import { LoadFailed } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { fluid } from '../styles/motion'; // [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { useRealtime } from '../hooks/useRealtime'; // [LAST-ACTIVITY 2026-10-16]
import { fluidDialog, fluidScrim, fluidCascade, useLandWindow, useRowsLand } from '../components/Fluid'; // [FLUID-3 2026-10-12] [FLUID-6 2026-10-12] useRowsLand
import {
  fetchUsers,
  updateDirectoryUser,
  createDirectoryUser,
  deleteDirectoryUser,
  DirectoryUser,
} from '../services/api';

/* ─────────────────────────── layout ─────────────────────────── */

// Fills the card and freezes the header/toolbar + table column header; only the
// body (table rows or team cards) scrolls — mirrors Payments/Refunds/etc.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1);

  /* [PHONE-LAYOUT 2026-10-24, user] phones: NOTHING is fixed at the top — the title, Add person, the search, the
     chips and the people table are ONE scroll, as Payments ("in the phone view nothing will be fixed on the top
     side otherwise how will i see the data for the below"); they took ~345 px and left the rows the rest. */
  @media (max-width: 768px) {
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

// The frozen top block — title + toolbar. Sits outside the scroll area (so it
// never scrolls); the table's sticky <thead> freezes the column header below it.
const FrozenTop = styled.div`
  flex-shrink: 0;
  padding: 20px 24px 0;
  background: ${(p) => p.theme.surface};

  @media (max-width: 600px) {
    padding: 16px 12px 0;
  }
`;

// Body scroll area for the non-table views (skeleton / team cards / empty state).
// The bottom pad clears the floating dock so the last item sits above the pill.
const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll; /* [SCROLLBAR 2026-07-08] always show the vertical scroller on desktop */
  padding: 4px 24px 88px;

  @media (max-width: 600px) {
    padding: 4px 12px 88px;
  }

  /* [PHONE-LAYOUT 2026-10-24] Phones: as tall as what it holds (the team cards); the page scrolls (Container). */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-y: hidden;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  /* [PHONE-LAYOUT 2026-10-24, user] Phones: the page head centred like every other tab (title, then its line), and
     Add person the full width under it. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    gap: 12px;
  }
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: ${(p) => p.theme.text};
  letter-spacing: -0.2px;
`;

const Sub = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${(p) => p.theme.muted};
`;

// [BUTTON-STYLE 2026-09-30] One button style: the green primary from styles/buttons.
const AddButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */

  /* [PHONE-LAYOUT 2026-10-24] Phones: the full width of the head, label centred. */
  @media (max-width: 768px) {
    width: 100%;
    min-height: 40px;
    justify-content: center;
  }
`;

/* soft glass strip — per design-liquid-glass "soft glass strip" recipe */
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-radius: 14px;
  margin-bottom: 14px;
  background: ${(p) =>
    p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.35) 100%)'};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;

  i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: ${(p) => p.theme.muted};
  }
`;

const Search = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 12px 0 34px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.65)')};
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
  font-size: 13px;
  font-weight: 500;
  outline: none;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &::placeholder { color: ${(p) => p.theme.muted}; }
  &:focus {
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)')};
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSunken}
  border-radius: 999px;
`;

const Chips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Chip = styled.button<{ $active: boolean }>`
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid
    ${(p) => (p.$active ? p.theme.green600 : p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')};
  background: ${(p) =>
    p.$active
      ? p.theme.colorScheme === 'dark'
        ? 'rgba(70,191,130,0.18)'
        : 'rgba(26,159,125,0.12)'
      : 'transparent'};
  color: ${(p) =>
    p.$active ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600) : p.theme.muted};
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover { color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)}; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.08' : '0.65')}), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.12' : '0.85')}), 0 1px 2px rgba(0,0,0,0.08);
`;

/* ─────────────────────────── table ─────────────────────────── */

// Body scroll area for the table view — identical to Refunds' ScrollContainer:
// edge-to-edge, scrolls BOTH axes (sticky <thead> freezes the column header
// vertically, sticky Person column freezes horizontally), and runs to the very
// bottom BEHIND the floating dock. The padding-bottom lifts the last row above
// the dock once you scroll to the end so nothing hides behind the pill.
const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-bottom: 88px;

  /* [PHONE-LAYOUT 2026-10-24, user] Phones: as tall as its rows (no box of its own to scroll up / down in): the
     table only swipes sideways here and the page scrolls (Container). The 88 px end pad still lifts the last row
     above the dock; the column header scrolls away with the page, as on Payments. */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th<{ $center?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${(p) => p.theme.pill};
  padding: 11px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  white-space: nowrap;
  text-align: ${(p) => (p.$center ? 'center' : 'left')};
  border-bottom: 1px solid ${(p) => p.theme.line};
  /* Cover the 1px sub-pixel seam Chromium can leave between sticky <th> cells
     (it showed the darker surface behind as faint vertical lines at random
     column boundaries). Painting the pill colour 1px past the right edge makes
     adjacent header cells read as one solid bar. */
  box-shadow: 1px 0 0 0 ${(p) => p.theme.pill};
`;

const ThPerson = styled(Th)`
  left: 0;
  z-index: 3;
  text-align: left;
  min-width: 240px;

  /* [UM-MOBILE-SCROLL 2026-07-07] On mobile the Person column scrolls
     horizontally with the rest of the table instead of being frozen, so the
     whole table moves as one. Drop only the left freeze — the header stays
     pinned to the top (sticky top from Th) like every other column. */
  @media (max-width: 768px) {
    left: auto;
  }
`;

const Tr = styled.tr`
  & > td { transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1); }
  /* Row-hover tint gated by html.pointer-active (utils/hoverGate.ts) +
     hover:hover so it can never stick on a row after a scroll/reload and touch
     devices never get a sticky highlight. */
  @media (hover: hover) {
    html.pointer-active &:hover td:not(:first-child) {
      background: ${(p) => (p.theme.colorScheme === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)')};
    }
  }
`;

const Td = styled.td<{ $center?: boolean }>`
  padding: 9px 12px;
  font-size: 13px;
  color: ${(p) => p.theme.text};
  border-bottom: 1px solid ${(p) => p.theme.line};
  text-align: ${(p) => (p.$center ? 'center' : 'left')};
  white-space: nowrap;
`;

const TdPerson = styled(Td)`
  position: sticky;
  left: 0;
  z-index: 1;
  background: ${(p) => p.theme.surface};
  min-width: 240px;

  /* [UM-MOBILE-SCROLL 2026-07-07] Un-freeze the Person cells on mobile so the
     column scrolls with the table (mirrors ThPerson above). */
  @media (max-width: 768px) {
    position: static;
  }
`;

const PersonCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
`;

const PersonName = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${(p) => p.theme.text};
  line-height: 1.2;
`;

const PersonEmail = styled.div`
  font-size: 11.5px;
  color: ${(p) => p.theme.muted};
  line-height: 1.3;
`;

const TypeChip = styled.span<{ $role: string | null }>`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #fff;
  background: ${(p) =>
    p.$role === 'pm'
      ? 'linear-gradient(180deg,' + p.theme.green600 + ',' + p.theme.green700 + ')' /* [GRAPHITE-EMERALD] */
      : p.$role === 'sales'
      ? 'linear-gradient(180deg,#0ea5e9,#0284c7)'
      : p.$role === 'both'
      ? 'linear-gradient(180deg,#a78bfa,#7c3aed)'
      : p.$role === 'leads' /* [LEADS-ROLE 2026-10-13] amber — apart from PM green / Sales blue */
      ? 'linear-gradient(180deg,#f59e0b,#d97706)'
      : (p.theme.colorScheme === 'dark' ? 'linear-gradient(180deg,#a2a2a2,#737373)' : 'linear-gradient(180deg,#9ea3a1,#6f7472)')}; /* others — neutral grey */

  /* [PHONE-LAYOUT 2026-10-24] Phones: 11 px, the phone minimum for labels (was 10.5). */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const Switch = styled.button<{ $on: boolean }>`
  position: relative;
  width: 42px;
  height: 23px;
  border-radius: 999px;
  border: none;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  background: ${(p) =>
    p.$on
      ? `linear-gradient(180deg, ${p.theme.green600}, ${p.theme.green700})`
      : p.theme.colorScheme === 'dark'
      ? 'rgba(255,255,255,0.18)'
      : 'rgba(0,0,0,0.18)'};
  opacity: ${(p) => (p.disabled ? 0.55 : 1)};
  transition: background 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? '21px' : '2px')};
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transition: left 0.2s cubic-bezier(0.32, 0.72, 0, 1);
  }

  /* [PHONE-LAYOUT 2026-10-24] Phones: an invisible 37 px tall finger area around the 42 x 23 switch (same look). */
  @media (max-width: 768px) {
    &::before {
      content: '';
      position: absolute;
      inset: -7px -4px;
    }
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  /* a shine on the track — ON keeps its green under it ("th admin switvh is not highlighting differently": the shine alone
     had replaced the green, so on and off looked the same) */
  background-image: linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0) 60%)${(p) => (p.$on ? `, linear-gradient(180deg, ${p.theme.green600}, ${p.theme.green700})` : '')};
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.18);
`;

const Check = styled.button<{ $on: boolean; $locked?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 7px;
  display: inline-grid;
  vertical-align: middle;
  place-items: center;
  cursor: ${(p) => (p.$locked ? 'not-allowed' : 'pointer')};
  border: 1px solid
    ${(p) =>
      p.$on ? p.theme.green600 : p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'};
  background: ${(p) => (p.$on ? `linear-gradient(180deg, ${p.theme.green600}, ${p.theme.green700})` : 'transparent')};
  color: #fff;
  font-size: 11px;
  opacity: ${(p) => (p.$locked ? 0.5 : 1)};
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: ${(p) => (p.$locked ? undefined : p.theme.green600)};
  }

  i { line-height: 1; }

  /* [PHONE-LAYOUT 2026-10-24] Phones: an invisible 36 px finger area around the 22 px box (same look). */
  @media (max-width: 768px) {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      inset: -7px;
    }
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.35);
`;

const DeleteBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.muted};
  cursor: pointer;
  font-size: 13px;
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => p.theme.danger};
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(220,38,38,0.14)' : 'rgba(220,38,38,0.08)')};
  }

  &:disabled { opacity: 0.3; cursor: not-allowed; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
`;

const StateNote = styled.div`
  padding: 56px 24px;
  text-align: center;
  color: ${(p) => p.theme.muted};
  font-size: 14px;
`;

/* ─────────────────────────── teams view ─────────────────────────── */

const TeamsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const TeamsHint = styled.div`
  font-size: 12.5px;
  color: ${(p) => p.theme.muted};
  padding: 0 2px;
`;

const TeamCard = styled.div`
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px;
  overflow: hidden;
  background: ${(p) => p.theme.surface};
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

/* PM (team owner) header — sits atop each card */
const TeamHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${(p) => p.theme.pill};
  border-bottom: 1px solid ${(p) => p.theme.line};
`;

const TeamMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const TeamCount = styled.span`
  flex-shrink: 0;
  font-size: 11.5px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
`;

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid ${(p) => p.theme.line};

  &:last-child { border-bottom: none; }
  &:hover {
    background: ${(p) => (p.theme.colorScheme === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)')};
  }

  /* [PHONE-LAYOUT 2026-10-24] Phones (PM Teams / Sales Team): the member's sections (AccessHint) drop to a full line
     of their own under the name — beside it they were squeezed into a narrow column that ran over the email. */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    row-gap: 6px;
    padding: 10px 12px;
  }
`;

const MemberMeta = styled.div`
  flex: 1;
  min-width: 0;

  /* [PHONE-LAYOUT 2026-10-24] Phones: a long email wraps instead of running past its box. */
  @media (max-width: 768px) {
    & > * { white-space: normal; overflow-wrap: anywhere; }
  }
`;

const AccessHint = styled.span`
  flex-shrink: 1;
  max-width: 45%;
  text-align: right;
  font-size: 11.5px;
  line-height: 1.35;
  color: ${(p) => p.theme.muted};

  /* [PHONE-LAYOUT 2026-10-24] Phones: a full line under the member, read left to right (see MemberRow). */
  @media (max-width: 768px) {
    flex: 1 0 100%;
    max-width: none;
    text-align: left;
  }
`;

const EmptyMembers = styled.div`
  padding: 14px 16px;
  font-size: 12.5px;
  color: ${(p) => p.theme.muted};
`;

/* ─────────────────────────── add modal (glass spec) ─────────────────────────── */

// [AUTO-REFRESH 2026-08-03] See reloadGuard — blocks the auto-reload while this modal is open.
// [FLUID-3 2026-10-12, user] The shade fades in and out with the fluid recipe (components/Fluid.tsx) and takes no
// clicks while it closes; the window used to pop in and vanish on close — now held mounted for its close.
const Overlay = styled.div.attrs<{ 'data-reload-block'?: string; $open: boolean }>({ 'data-reload-block': '' })`
  position: fixed;
  inset: 0;
  /* Must stay below the GlassSelect popover (z-index 1200, portaled to <body>)
     so the Type dropdown renders ABOVE the modal instead of behind it. Still
     above all page content + other dialog overlays. (The shared toast host sits
     at 3000, so its confirmations float above this modal.) */
  z-index: 1190;
  display: grid;
  place-items: center;
  padding: 24px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(20,30,25,0.18)')};
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  ${(p) => fluidScrim(p.$open)}
`;

// [FLUID-3 2026-10-12] $land = the blocks may rise in now: only just after the window opens, so an error line
// that appears later (a failed add) shows at once, as before, instead of rising in late.
const Panel = styled.div<{ $open: boolean; $land: boolean }>`
  position: relative;
  width: 100%;
  max-width: 420px;
  padding: 22px;
  border-radius: 20px;
  isolation: isolate;
  background: ${(p) =>
    p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(86, 86, 86, 0.42) 0%, rgba(54, 54, 54, 0.55) 100%)'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(230, 233, 231, 0.68) 100%)'};
  backdrop-filter: blur(48px) saturate(180%);
  -webkit-backdrop-filter: blur(48px) saturate(180%);
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.65)')};
  box-shadow:
    0 30px 80px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.22)')},
    inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)')};
  /* [FLUID-3 2026-10-12, user] Rises 12px and grows from 0.95 on the spring (readable after ~0.1 s, open by
     0.36 s), closes in 0.18 s; the title, fields and buttons rise in one after another. Replaces the 0.22 s
     popIn keyframe (which had no exit). */
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open && p.$land ? fluidCascade('> *', 0, 20, 6) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(0,0,0,0) 80%, rgba(0,0,0,0.12) 100%);
    pointer-events: none;
    z-index: 0;
  }
  & > * { position: relative; z-index: 1; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSurface(36)}
  border-color: transparent;
`;

/** [FLUID-3 2026-10-12] The window's content, kept from its last open render while the window plays its close:
 *  a successful add empties the form the moment it closes, and the box should fade out showing what it showed.
 *  The closing window is inert, so nothing in the kept copy can be clicked or typed into. (The same helper as
 *  InventoryPage's.) */
const KeepWhileClosing: React.FC<{ open: boolean; children: React.ReactNode }> = ({ open, children }) => {
  const kept = useRef<React.ReactNode>(children);
  if (open) kept.current = children;
  return <>{kept.current}</>;
};

const ModalTitle = styled.h2`
  margin: 0 0 4px;
  font-size: 17px;
  font-weight: 700;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
  text-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? '0 1px 2px rgba(0,0,0,0.45)' : 'none')};
`;

const ModalSub = styled.p`
  margin: 0 0 18px;
  font-size: 12.5px;
  color: ${(p) => p.theme.muted};
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.65)')};
  backdrop-filter: blur(8px);
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
  font-size: 13px;
  font-weight: 500;
  outline: none;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)')};
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSunken}
`;

/* GlassSelect tuned to match the flat 38px Input above, so the Type dropdown
   lines up with the Full name / Email fields in the Add-person modal. */
const Select = styled(GlassSelect)`
  && {
    min-width: 100%;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    height: 38px;
    padding: 0 32px 0 12px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    justify-content: flex-start;
    border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.65)')};
    background-image: none;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
    box-shadow: none;
    transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  &&[aria-expanded='true'],
  &&:focus,
  &&:focus-visible {
    outline: none;
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)')};
  }
`;

const ModalError = styled.div`
  font-size: 12.5px;
  color: ${(p) => p.theme.danger};
  margin: -6px 0 12px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
`;

// [BUTTON-STYLE 2026-09-30] Cancel = the secondary style.
const GhostBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
`;

// [BUTTON-STYLE 2026-09-30] The green primary.
const PrimaryBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
`;

/* ─────────────────────────── component ─────────────────────────── */

type Filter = 'all' | 'pm' | 'sales' | 'leads' | 'others' | 'teams' | 'sales-teams';

const ROLE_FULL: Record<string, string> = { pm: 'PM', sales: 'Sales', both: 'PM + Sales', leads: 'Leads' };

function initialOf(u: DirectoryUser): string {
  return (u.name?.trim()[0] || u.email[0] || '?').toUpperCase();
}

/* [LAST-ACTIVITY 2026-10-16, user] "date and time": the Last activity column reads like the Application Logs' When
   column — "Oct 16, 2026" over "10:28 PM IST" — always in IST (the team's clock, as the header clock and the App Log),
   whatever the viewer's machine is set to. Formatters built once: a toLocale* call with options builds a new one each
   time. [DATE-STYLE 2026-09-29, user] 'en-US', not the browser's locale: an Indian-English browser wrote "29 Sept 2026". */
const LAST_ACTIVITY_DATE_IST_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: 'numeric' });
const LAST_ACTIVITY_TIME_IST_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true });

function fmtLastActivity(s: string | null): { date: string; time: string } {
  if (!s) return { date: 'Never', time: '' };
  const d = new Date(s); // the API sends an ISO instant, e.g. "2026-10-16T17:08:35.000Z"
  if (isNaN(d.getTime())) return { date: '—', time: '' };
  return { date: LAST_ACTIVITY_DATE_IST_FMT.format(d), time: `${LAST_ACTIVITY_TIME_IST_FMT.format(d)} IST` };
}

/* [LAST-ACTIVITY 2026-10-16] Date over time, both centred; the time a touch smaller and lighter. Kept at ~30 px tall,
   under the Person cell's 31 px, so the two lines never make a row taller (measured in a real browser: at line-height
   1.3 + a 1 px gap it was 32 px and 57 of 65 rows grew from 50 to 51 px). */
const LastActivity = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.75;
  font-size: 12.5px;
  line-height: 1.25;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  small {
    font-size: 11.5px;
    opacity: 0.8;
  }
`;

const SECTION_LABEL: Record<string, string> = Object.fromEntries(SECTIONS.map((s) => [s.key, s.label]));

/** Access summary shown next to each team member — the section names they can
 *  see, with Refunds + Disputes shown as one "Refunds/Disputes" (matching the
 *  merged tab) when the user has both; a lone half falls back to its own name. */
function accessHint(u: DirectoryUser): string {
  if (u.is_admin) return 'Admin · all sections';
  if (u.allowed_sections.length === 0) return 'No access';
  const allowed = new Set(u.allowed_sections);
  const labels: string[] = [];
  const covered = new Set<string>();
  for (const g of SECTION_GROUPS) {
    if (g.keys.every((k) => allowed.has(k))) {
      labels.push(g.label);
      g.keys.forEach((k) => covered.add(k));
    }
  }
  for (const k of u.allowed_sections) {
    if (!covered.has(k)) labels.push(SECTION_LABEL[k] || k);
  }
  return labels.join(', ');
}

/** Drop the " - PM" suffix for display: "Team Ilma - PM" → "Team Ilma". */
function teamLabel(team: string): string {
  return team.replace(/\s*-\s*PM$/i, '').trim();
}

/** Drop the " - Sales" suffix for display: "Team Aman - Sales" → "Team Aman".
 *  "IC - Sales" → "IC", "Team ROW" → "Team ROW" (no suffix to strip). */
function salesTeamLabel(team: string): string {
  return team.replace(/\s*-\s*Sales$/i, '').trim();
}

/**
 * The Sales team's lead — mirror of findOwner for "Team <Name> - Sales" teams
 * (the member whose name starts with <Name>). "IC - Sales" and "Team ROW" have
 * no single owner, so this returns null and the card shows "No single lead".
 */
function findSalesOwner(team: string, members: DirectoryUser[]): DirectoryUser | null {
  const m = team.match(/^Team\s+(.+?)\s*-\s*Sales$/i);
  if (!m) return null;
  const lead = m[1].trim().toLowerCase();
  return members.find((u) => (u.name || '').toLowerCase().startsWith(lead)) || null;
}

/**
 * PM teams whose "Team <Lead> - PM" name doesn't resolve to a real person but that
 * have a designated lead, keyed by exact HubSpot team name → lead email.
 * "Team ROW - PM" is led by Hamza Shariq (mirrors EXPLICIT_TEAM_LEADS in the
 * server's inventoryScope).
 */
const EXPLICIT_TEAM_LEADS: Record<string, string> = {
  'Team ROW - PM': 'hamza.shariq@thescale.agency',
};

/**
 * The team's owner (lead). For a "Team <Name> - PM" team it's the member whose
 * name starts with <Name> (e.g. "Team Shreyansh - PM" → Shreyansh Gupta).
 * Teams in EXPLICIT_TEAM_LEADS (e.g. Team ROW → Hamza) use the designated email.
 * "IC - PM" has no single owner, so this returns null.
 */
function findOwner(team: string, members: DirectoryUser[]): DirectoryUser | null {
  const explicit = EXPLICIT_TEAM_LEADS[team];
  if (explicit) {
    return members.find((u) => u.email.toLowerCase() === explicit.toLowerCase()) || null;
  }
  const m = team.match(/^Team\s+(.+?)\s*-\s*PM$/i);
  if (!m) return null;
  const lead = m[1].trim().toLowerCase();
  return members.find((u) => (u.name || '').toLowerCase().startsWith(lead)) || null;
}

/** [FLUID-6 2026-10-12] The header's "Add person" button and its window, as one small component. The window's open state
 *  and its form used to live in the page, so opening it (twice: mount, then the open motion), every keystroke in it and
 *  closing it re-rendered the whole page with every person's row — measured 87 + 60 ms per open on a dev build with 33
 *  people (the live list has 56). Here they re-render only this. A successful add hands the new person to the page. */
const AddPersonButton: React.FC<{ onCreated: (created: DirectoryUser) => void }> = ({ onCreated }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addType, setAddType] = useState<'pm' | 'sales' | 'leads' | 'others'>('others');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // [FLUID-3 2026-10-12] The Add window stays mounted through its 0.18 s close; its blocks rise in only while it
  // has just opened (see Panel's $land).
  const addPres = usePresence(addOpen, fluid.dialogOutMs);
  const addLand = useLandWindow(addOpen, 'add');

  const submitAdd = async () => {
    setAddError(null);
    const email = addEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAddError('Enter a valid email address.');
      return;
    }
    setAdding(true);
    try {
      const role = addType === 'others' ? null : addType;
      const created = await createDirectoryUser(email, addName.trim(), role);
      onCreated(created);
      setAddOpen(false);
      setAddName('');
      setAddEmail('');
      setAddType('others');
      toastSuccess('Person added');
    } catch (e: any) {
      setAddError(e?.response?.data?.error || 'Could not add person.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <AddButton onClick={() => setAddOpen(true)}>
        <i className="fas fa-user-plus" /> Add person
      </AddButton>
      {/* Portaled to <body> so the fixed overlay anchors to the viewport, not the
          transformed PageFade ancestor (which would centre it mid-page).
          [FLUID-3 2026-10-12] Held mounted through its close (addPres); inert and showing its last open content
          while it goes — a successful add empties the form at the same moment. */}
      {addPres.mounted && createPortal(
        <Overlay onMouseDown={(e) => e.target === e.currentTarget && setAddOpen(false)} $open={addOpen && addPres.entered} inert={!addOpen}>
          <Panel $open={addOpen && addPres.entered} $land={addLand}>
            <KeepWhileClosing open={addOpen}>
            <ModalTitle>Add a person</ModalTitle>
            <ModalSub>For someone not already in the list. They'll start with no access until you grant sections.</ModalSub>
            <Field>
              <Label>Full name</Label>
              <Input
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="e.g. Jane Doe"
              />
            </Field>
            <Field>
              <Label>Email</Label>
              <Input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="jane@company.com"
                onKeyDown={(e) => e.key === 'Enter' && submitAdd()}
              />
            </Field>
            <Field>
              <Label>Type</Label>
              <Select liquidList value={addType} onChange={(e) => setAddType(e.target.value as 'pm' | 'sales' | 'leads' | 'others')}>
                <option value="pm">PM</option>
                <option value="sales">Sales</option>
                {/* [LEADS-ROLE 2026-10-13, user] "when i add the name then also it should ask for this lead thing" */}
                <option value="leads">Leads</option>
                <option value="others">Others</option>
              </Select>
            </Field>
            {addError && <ModalError>{addError}</ModalError>}
            <ModalActions>
              <GhostBtn onClick={() => setAddOpen(false)}>Cancel</GhostBtn>
              <PrimaryBtn onClick={submitAdd} disabled={adding}>
                {adding ? 'Adding…' : 'Add person'}
              </PrimaryBtn>
            </ModalActions>
            </KeepWhileClosing>
          </Panel>
        </Overlay>,
        document.body
      )}
    </>
  );
};

const UserManagementPage: React.FC = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  // [FLUID-6 2026-10-12] The Add person button + window live in AddPersonButton (above): opening it, typing in it and
  // closing it re-render only that small component, never this page and its people list.
  const onPersonAdded = useCallback((created: DirectoryUser) => {
    setUsers((prev) => [created, ...prev.filter((u) => u.id !== created.id)]);
  }, []);

  // [FLUID-6 2026-10-12, user] "user management page also not having animations". Counts the times people land for the
  // person to see: each load (a tab visit, a retry, the reload after a failed save — always after the skeleton). Each
  // count runs the row wave on the people table (landRef): every row on screen rises in, top to bottom
  // (components/Fluid.tsx landRows). The user, same day: "when I change this [the filter chips], don't show animations"
  // — a chip only swaps the list (and the Teams views their cards) with no motion. Never on a save, an add, a remove or
  // typing in the search either.
  const [landing, setLanding] = useState(0);
  // The skeleton is up: the next people to land rise in. Once per skeleton, so a second answer to the same load (the
  // development double mount) updates the rows in place instead of starting the wave again.
  const landPendingRef = useRef(false);
  const landRef = useRef<HTMLDivElement>(null);
  useRowsLand(landRef, landing > 0 ? landing : null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    landPendingRef.current = true; // [FLUID-6 2026-10-12]
    try {
      const { users } = await fetchUsers();
      setUsers(users);
      // [FLUID-6 2026-10-12] The people that replace the skeleton rise in.
      if (landPendingRef.current) { landPendingRef.current = false; setLanding((n) => n + 1); }
    } catch (e: any) {
      setLoadError(e?.response?.data?.error || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // [LAST-ACTIVITY 2026-10-16, user] "it should also update with time if someone use the app". The server bumps the
  // `users` data version each time someone's activity is stamped (at most once a minute per person, only while they use
  // the app); this quietly re-reads the list and moves ONLY the Last activity values — no skeleton, no row wave, and a
  // switch an admin just flipped is never overwritten. At most one re-read per 10 s, however many people are active.
  const activityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activityReadAt = useRef(0);
  const refreshLastActivity = useCallback(async () => {
    activityReadAt.current = Date.now();
    try {
      const { users: fresh } = await fetchUsers();
      const seen = new Map(fresh.map((f) => [f.id, f.last_login_at] as const));
      setUsers((prev) => {
        let moved = false;
        const next = prev.map((u) => {
          const at = seen.get(u.id);
          if (at === undefined || at === u.last_login_at) return u;
          moved = true;
          return { ...u, last_login_at: at };
        });
        return moved ? next : prev;
      });
    } catch {
      /* a missed background re-read keeps what is on screen; the next bump catches up */
    }
  }, []);
  useRealtime(['users'], () => {
    if (activityTimer.current) return; // a re-read is already queued
    const wait = Math.max(0, activityReadAt.current + 10 * 1000 - Date.now());
    activityTimer.current = setTimeout(() => {
      activityTimer.current = null;
      void refreshLastActivity();
    }, wait);
  });
  useEffect(() => () => {
    if (activityTimer.current) clearTimeout(activityTimer.current);
  }, []);

  // Optimistic patch + immediate save. On failure, reload from server to resync.
  // Feedback goes through the shared top-center toast (toastService), like the
  // rest of the app — the optimistic UI is the "saving" signal, a toast confirms.
  const persist = useCallback(
    async (id: number, patch: Partial<DirectoryUser>) => {
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
      try {
        await updateDirectoryUser(id, {
          is_admin: patch.is_admin,
          is_senior: patch.is_senior,
          allowed_sections: patch.allowed_sections,
          visible_to_owner: patch.visible_to_owner,
        });
        toastSuccess('Saved');
      } catch (e: any) {
        toastError(e?.response?.data?.error || 'Save failed');
        load();
      }
    },
    [load]
  );

  const toggleAdmin = (u: DirectoryUser) => {
    if (me?.id === u.id) return; // can't change your own admin status
    persist(u.id, { is_admin: !u.is_admin });
  };

  // Senior: sees every team's Performance tab + every PM in My Inventory (default
  // to their own name), without full admin powers.
  const toggleSenior = (u: DirectoryUser) => {
    persist(u.id, { is_senior: !u.is_senior });
  };

  // Teams view: whether this member shows up in their team owner's (PM's) account.
  const toggleVisible = (u: DirectoryUser) => {
    persist(u.id, { visible_to_owner: !u.visible_to_owner });
  };

  // Grant/revoke a section GROUP. For the merged "Refunds/Disputes" group this
  // flips BOTH underlying keys together, so the grant matches the merged nav tab.
  const toggleSectionGroup = (u: DirectoryUser, keys: string[]) => {
    if (u.is_admin) return; // admins already have everything
    const hasAll = keys.every((k) => u.allowed_sections.includes(k));
    const next = hasAll
      ? u.allowed_sections.filter((k) => !keys.includes(k))
      : Array.from(new Set([...u.allowed_sections, ...keys]));
    persist(u.id, { allowed_sections: next });
  };

  const removeUser = async (u: DirectoryUser) => {
    if (me?.id === u.id) return;
    const ok = await confirmDialog({
      title: 'Remove user',
      message: (
        <>Remove <strong>{u.name || u.email}</strong>? They can sign in again later (and would re-appear with no access).</>
      ),
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteDirectoryUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toastSuccess('User removed');
    } catch (e: any) {
      toastError(e?.response?.data?.error || 'Delete failed');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !(u.name?.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))) return false;
      switch (filter) {
        case 'pm':
          return u.directory_role === 'pm' || u.directory_role === 'both';
        case 'sales':
          return u.directory_role === 'sales' || u.directory_role === 'both';
        case 'leads': // [LEADS-ROLE]
          return u.directory_role === 'leads';
        case 'others':
          return !u.directory_role;
        default:
          return true;
      }
    });
  }, [users, search, filter]);

  // [SEARCH-HIGHLIGHT 2026-09-29, user] Amber highlight of the searched name / email in the rows.
  const peopleTableRef = useRef<HTMLTableElement | null>(null);
  useSearchHighlight(peopleTableRef, search, loading ? null : filtered);

  // Teams view: group people by their real HubSpot PM team (pm_team), e.g.
  // "Team Shreyansh - PM", "Team Ilma - PM", "IC - PM". The lead is detected from
  // the team name; everyone else is a member with a visibility checkbox.
  const teamGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (u: DirectoryUser) =>
      !q || (u.name?.toLowerCase().includes(q) ?? false) || u.email.toLowerCase().includes(q);

    const byTeam = new Map<string, DirectoryUser[]>();
    for (const u of users) {
      if (!u.pm_team) continue;
      const arr = byTeam.get(u.pm_team);
      if (arr) arr.push(u);
      else byTeam.set(u.pm_team, [u]);
    }

    return [...byTeam.entries()]
      .map(([team, all]) => {
        const owner = findOwner(team, all);
        // The whole team, lead INCLUDED — the lead now shows as a row in the
        // table rather than a "Lead:" line in the header. Narrowed by search.
        const members = q ? all.filter(matches) : all;
        return { team, label: teamLabel(team), owner, members, total: all.length };
      })
      .filter((g) => g.members.length > 0)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users, search]);

  // Sales Team view: the sales mirror of teamGroups — group people by their live
  // HubSpot sales team (sales_team). Display-only for now (no visibility toggle:
  // that's a PM-owner concept with no sales equivalent yet).
  const salesTeamGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (u: DirectoryUser) =>
      !q || (u.name?.toLowerCase().includes(q) ?? false) || u.email.toLowerCase().includes(q);

    const byTeam = new Map<string, DirectoryUser[]>();
    for (const u of users) {
      if (!u.sales_team) continue;
      const arr = byTeam.get(u.sales_team);
      if (arr) arr.push(u);
      else byTeam.set(u.sales_team, [u]);
    }

    return [...byTeam.entries()]
      .map(([team, all]) => {
        const owner = findSalesOwner(team, all);
        // Lead INCLUDED in the table rows (mirrors the PM teams view).
        const members = q ? all.filter(matches) : all;
        return { team, label: salesTeamLabel(team), owner, members, total: all.length };
      })
      .filter((g) => g.members.length > 0)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users, search]);

  const renderMember = (m: DirectoryUser) => (
    <MemberRow key={m.id}>
      <Check
        $on={m.visible_to_owner}
        onClick={() => toggleVisible(m)}
        title={m.visible_to_owner ? 'Visible to team owner — click to hide' : 'Hidden from team owner — click to show'}
        aria-label={`${m.visible_to_owner ? 'Visible to' : 'Hidden from'} team owner: ${m.name || m.email}`}
      >
        {m.visible_to_owner && <i className="fas fa-check" />}
      </Check>
      <Avatar>{initialOf(m)}</Avatar>
      <MemberMeta>
        <PersonName>
          {m.name || m.email}
          {me?.id === m.id && <span style={{ opacity: 0.6, fontWeight: 500 }}> (you)</span>}
        </PersonName>
        <PersonEmail>{m.email}</PersonEmail>
      </MemberMeta>
      <AccessHint>{accessHint(m)}</AccessHint>
    </MemberRow>
  );

  // Sales Team member row — no visibility checkbox (display-only; the toggle is a
  // PM-team-owner control that doesn't apply to sales teams).
  const renderSalesMember = (m: DirectoryUser) => (
    <MemberRow key={m.id}>
      <Avatar>{initialOf(m)}</Avatar>
      <MemberMeta>
        <PersonName>
          {m.name || m.email}
          {me?.id === m.id && <span style={{ opacity: 0.6, fontWeight: 500 }}> (you)</span>}
        </PersonName>
        <PersonEmail>{m.email}</PersonEmail>
      </MemberMeta>
      <AccessHint>{accessHint(m)}</AccessHint>
    </MemberRow>
  );

  // [UM-CHIP-COUNTS 2026-07-02] Show each role chip's count too — same basis as "All"
  // (the full roster, ignoring search). A 'both' person counts under BOTH PMs and Sales,
  // matching how the pm/sales filters select them.
  const pmCount = users.filter((u) => u.directory_role === 'pm' || u.directory_role === 'both').length;
  const salesCount = users.filter((u) => u.directory_role === 'sales' || u.directory_role === 'both').length;
  const leadsCount = users.filter((u) => u.directory_role === 'leads').length; // [LEADS-ROLE]
  const othersCount = users.filter((u) => !u.directory_role).length;
  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: `All (${users.length})` },
    { key: 'pm', label: `PMs (${pmCount})` },
    { key: 'sales', label: `Sales (${salesCount})` },
    { key: 'leads', label: `Leads (${leadsCount})` },
    { key: 'others', label: `Others (${othersCount})` },
    { key: 'teams', label: 'PM Teams' },
    { key: 'sales-teams', label: 'Sales Team' },
  ];

  return (
    <Container>
      <FrozenTop>
      <HeaderRow>
        <TitleBlock>
          <Title>User Management</Title>
          <Sub>
            Grant each person the sections they can see, or mark them an admin for full access.
            Everyone signing in from an authorized domain appears here automatically.
          </Sub>
        </TitleBlock>
        <AddPersonButton onCreated={onPersonAdded} />
      </HeaderRow>

      <Toolbar>
        <SearchWrap>
          <i className="fas fa-search" />
          <Search
            placeholder="Search by name or email…"
            value={search}
            // Typing a search snaps the filter chip back to "All" so the search
            // spans every person, not just the currently selected group.
            onChange={(e) => { const v = e.target.value; setSearch(v); if (v) setFilter('all'); }}
          />
        </SearchWrap>
        {/* [FLUID-8 2026-10-12, user] InstantPick: a chip lights up on the mouse PRESS; the list follows as a transition (it
            used to light up only after the whole people list had re-rendered). Still no motion (user, FLUID-6). */}
        <InstantPick value={filter} onPick={setFilter}>
          {(shown, press) => (
            <Chips>
              {filters.map((f) => (
                <Chip key={f.key} $active={shown === f.key} {...press(f.key)}>
                  {f.label}
                </Chip>
              ))}
            </Chips>
          )}
        </InstantPick>
      </Toolbar>
      </FrozenTop>

      {loading ? (
        <TableScroll>
        <TableSkeleton
          rows={9}
          columns={[
            { label: 'Person' },
            { label: 'Type', align: 'center' },
            { label: 'Admin', align: 'center' },
            { label: 'Seniors', align: 'center' },
            ...SECTION_GROUPS.map((g) => ({ label: g.label, align: 'center' as const })),
            { label: 'Last activity', align: 'center' }, // [LAST-ACTIVITY 2026-10-16] was "Last login"
            { label: '', align: 'center' },
          ]}
        />
        </TableScroll>
      ) : loadError ? (
        // [LOAD-STATES 2026-09-29, user] Was the raw server text ("Internal server error").
        <Scroll><LoadFailed what="people" onRetry={load} /></Scroll>
      ) : filter === 'teams' ? (
        <Scroll>
        {teamGroups.length === 0 ? (
          <StateNote>
            No PM teams to show. (Teams come from HubSpot — if this is empty the roster may not be loaded yet.)
          </StateNote>
        ) : (
          <TeamsWrap>
            <TeamsHint>
              Tick a member to let their team lead see them in their own account. Untick to hide them.
            </TeamsHint>
            {teamGroups.map(({ team, label, owner, members }) => (
              <TeamCard key={team}>
                <TeamHead>
                  <Avatar>{owner ? initialOf(owner) : <i className="fas fa-users" />}</Avatar>
                  <TeamMeta>
                    <PersonName>{label}</PersonName>
                  </TeamMeta>
                  <TeamCount>
                    {members.length} member{members.length === 1 ? '' : 's'}
                  </TeamCount>
                </TeamHead>
                {members.length === 0 ? (
                  <EmptyMembers>No other members in this team.</EmptyMembers>
                ) : (
                  <MemberList>{members.map(renderMember)}</MemberList>
                )}
              </TeamCard>
            ))}
          </TeamsWrap>
        )}
        </Scroll>
      ) : filter === 'sales-teams' ? (
        <Scroll>
        {salesTeamGroups.length === 0 ? (
          <StateNote>
            No Sales teams to show. (Teams come from HubSpot — if this is empty the roster may not be loaded yet.)
          </StateNote>
        ) : (
          <TeamsWrap>
            <TeamsHint>Live Sales teams from HubSpot.</TeamsHint>
            {salesTeamGroups.map(({ team, label, owner, members }) => (
              <TeamCard key={team}>
                <TeamHead>
                  <Avatar>{owner ? initialOf(owner) : <i className="fas fa-users" />}</Avatar>
                  <TeamMeta>
                    <PersonName>{label}</PersonName>
                  </TeamMeta>
                  <TeamCount>
                    {members.length} member{members.length === 1 ? '' : 's'}
                  </TeamCount>
                </TeamHead>
                {members.length === 0 ? (
                  <EmptyMembers>No other members in this team.</EmptyMembers>
                ) : (
                  <MemberList>{members.map(renderSalesMember)}</MemberList>
                )}
              </TeamCard>
            ))}
          </TeamsWrap>
        )}
        </Scroll>
      ) : filtered.length === 0 ? (
        <Scroll><StateNote>No people match your search.</StateNote></Scroll>
      ) : (
        <TableScroll ref={landRef /* [FLUID-6 2026-10-12] the row wave's root — the people table only, never the skeleton */}>
          <Table ref={peopleTableRef /* [SEARCH-HIGHLIGHT 2026-09-29] */}>
            <thead>
              <tr>
                <ThPerson as="th">Person</ThPerson>
                <Th $center>Type</Th>
                <Th $center>Admin</Th>
                <Th $center>Seniors</Th>
                {SECTION_GROUPS.map((g) => (
                  <Th key={g.id} $center>
                    {g.label}
                  </Th>
                ))}
                <Th $center>Last activity</Th>{/* [LAST-ACTIVITY 2026-10-16] was "Last login" */}
                <Th $center> </Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isSelf = me?.id === u.id;
                const when = fmtLastActivity(u.last_login_at); // [LAST-ACTIVITY 2026-10-16]
                return (
                  <Tr key={u.id}>
                    <TdPerson>
                      <PersonCell>
                        <Avatar>{initialOf(u)}</Avatar>
                        <div style={{ minWidth: 0 }}>
                          <PersonName>
                            {u.name || u.email}
                            {isSelf && <span style={{ color: 'inherit', opacity: 0.6, fontWeight: 500 }}> (you)</span>}
                          </PersonName>
                          <PersonEmail>{u.email}</PersonEmail>
                        </div>
                      </PersonCell>
                    </TdPerson>

                    <Td $center>
                      <TypeChip $role={u.directory_role}>
                        {u.directory_role ? ROLE_FULL[u.directory_role] : 'Others'}
                      </TypeChip>
                    </Td>

                    <Td $center>
                      <Switch
                        $on={u.is_admin}
                        disabled={isSelf}
                        onClick={() => toggleAdmin(u)}
                        title={isSelf ? "You can't change your own admin access" : u.is_admin ? 'Remove admin' : 'Make admin'}
                        aria-label="Toggle admin"
                      />
                    </Td>

                    <Td $center>
                      <Switch
                        $on={u.is_senior}
                        disabled={u.is_admin}
                        onClick={() => !u.is_admin && toggleSenior(u)}
                        title={
                          u.is_admin
                            ? 'Admins already see everything'
                            : u.is_senior
                            ? 'Remove senior access (all teams + every PM)'
                            : 'Make senior — sees all teams’ Performance + every PM in My Inventory'
                        }
                        aria-label="Toggle senior"
                      />
                    </Td>

                    {SECTION_GROUPS.map((g) => {
                      const on = u.is_admin || g.keys.every((k) => u.allowed_sections.includes(k));
                      return (
                        <Td key={g.id} $center>
                          <Check
                            $on={on}
                            $locked={u.is_admin}
                            onClick={() => toggleSectionGroup(u, g.keys)}
                            aria-label={`${on ? 'Granted' : 'Not granted'}: ${g.label}`}
                          >
                            {on && <i className="fas fa-check" />}
                          </Check>
                        </Td>
                      );
                    })}

                    {/* [LAST-ACTIVITY 2026-10-16, user] Date over time (IST), kept live by the useRealtime(['users']) re-read */}
                    <Td $center style={{ color: 'inherit' }}>
                      <LastActivity>
                        {when.date}
                        {when.time && <small>{when.time}</small>}
                      </LastActivity>
                    </Td>

                    <Td $center>
                      <DeleteBtn
                        disabled={isSelf}
                        onClick={() => removeUser(u)}
                        title={isSelf ? "You can't remove yourself" : 'Remove user'}
                      >
                        <i className="fas fa-trash-alt" />
                      </DeleteBtn>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </TableScroll>
      )}

    </Container>
  );
};

export default UserManagementPage;
